import classNames from 'classnames';

import lang from '../../../../lib/language';
import { getLimitedText } from '../../../../lib/util/DataHelpers';
import { isEmpty } from '../../../../lib/util/typeGuards';

import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import useBrowserWidth from '../../../hooks/useBrowserWidth';
import useIsMobile from '../../../hooks/useIsMobile';
import NoDataAvailable from '../../../NoDataAvailable/NoDataAvailable';
import {
  WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_LIMIT,
  WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_MOB_LIMIT,
  WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_NOT_STANDART_LIMIT,
  WEEK52_GAINERS_AND_LOSERS_TABLE_HEADERS,
} from '../../Week52GainersAndLosers.constants';
import { Week52GainersAndLosersListProps, Week52Row } from '../../Week52GainersAndLosers.types';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import styles from '../../Week52GainersAndLosers.module.scss';

const MINIMUM_RESULTS = 4;
const NARROW_DESKTOP_MIN_WIDTH = 1300;
const NARROW_DESKTOP_MAX_WIDTH = 1400;
const SMALL_DESKTOP_MAX_WIDTH = 1000;
const LOADER_SYMBOL_WIDTH = 48;
const LOADER_NAME_WIDTH_MOBILE = 80;
const LOADER_NAME_WIDTH = 100;
const LOADER_PRICE_WIDTH_MOBILE = 48;
const LOADER_PRICE_WIDTH = 68;
const LOADER_CHANGE_WIDTH = 60;

const Week52GainersAndLosersList = ({
  data,
  title,
  isReady,
  onSymbolClick,
}: Week52GainersAndLosersListProps) => {
  const isReadyAndNoData = isReady && isEmpty(data);
  const { isMobile } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();

  function getSymbolNameLimit() {
    if (isMobile) {
      return WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_MOB_LIMIT;
    }

    if ((browserWidthSize > NARROW_DESKTOP_MIN_WIDTH && browserWidthSize < NARROW_DESKTOP_MAX_WIDTH)
      || browserWidthSize < SMALL_DESKTOP_MAX_WIDTH) {
      return WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_NOT_STANDART_LIMIT;
    }

    return WEEK52_GAINERS_AND_LOSERS_SYMBOL_NAME_LIMIT;
  }
  const tableHeaders1 = isMobile
    ? ['', '', '']
    : [title].map((item: string) => <div className={styles.tableTitle} key={item}>{item}</div>);
  const tableHeaders2 = WEEK52_GAINERS_AND_LOSERS_TABLE_HEADERS.map((item: string) => (
    <div
      className={classNames({
        [styles.headerTh]: !isMobile,
      })}
      key={item}
    >
      {item}
    </div>
  ));

  const SkeletonLoaderRow = (
    <div className={styles.rowWrapper}>
      <div className={styles.wrapperInfo}>
        <div className={styles.wrapperCompany}>
          <div className={styles.loaderLogo} />
          <div className={styles.companyInfo}>
            <div className={classNames(styles.loader, styles.loaderMargin)} style={{ width: LOADER_SYMBOL_WIDTH }} />
            <div
              className={styles.loader}
              style={{ width: isMobile ? LOADER_NAME_WIDTH_MOBILE : LOADER_NAME_WIDTH }}
            />
          </div>
        </div>
      </div>
      <div className={styles.loaderSmallWrapper}>
        <div
          className={styles.loader}
          style={{ width: isMobile ? LOADER_PRICE_WIDTH_MOBILE : LOADER_PRICE_WIDTH }}
        />
      </div>
      <div className={styles.loaderSmallWrapper}>
        <div className={styles.wrapperRow}>
          <div>
            <div className={classNames(styles.loader, styles.loaderMargin)} style={{ width: LOADER_CHANGE_WIDTH }} />
            <div className={styles.loader} style={{ width: LOADER_CHANGE_WIDTH }} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderList = () => {
    const rows = data.map((item: Week52Row): JSX.Element => {
      const { logo, url } = item;
      const displayName = getLimitedText(item.displayName, getSymbolNameLimit());

      return (
        <ClickableDiv
          className={styles.rowWrapper}
          key={item.symbol}
          onClick={() => onSymbolClick(item.symbol)}
        >
          <div className={styles.wrapperInfo}>
            <div className={styles.wrapperCompany}>
              {(url || logo) && (
                <CompanyLogo url={url} logo={logo} symbol={item.symbol} />
              )}
              <div className={styles.companyInfo}>
                <p className={styles.symbol}>{item.symbol}</p>
                <p className={classNames(styles.symbol, styles.name)}>{displayName}</p>
              </div>
            </div>
          </div>
          <PriceAndChange item={item} />
        </ClickableDiv>
      );
    });

    if (rows.length < MINIMUM_RESULTS) {
      rows.push(
        ...Array.from(
          { length: MINIMUM_RESULTS - rows.length },
          (_, i) => (
            <div
              key={`${rows.length + i}`}
              className={classNames(styles.row, styles.emptyRow)}
            />
          ),
        ),
      );
    }

    return rows;
  };

  return (
    <div
      className={classNames(styles.table, {
        [styles.tableMobile]: isMobile && data.length >= MINIMUM_RESULTS,
        [styles.tableUnderMinimumResults]: isMobile && data.length < MINIMUM_RESULTS,
      })}
    >
      {
        isReadyAndNoData ? (
          <NoDataAvailable
            className={styles.noDataToDisplay}
            isNoDataIcon
            title={lang.commonDiscover52WeekNoData()}
          />
        ) : (
          <>
            <div className={styles.tableHead}>
              <div className={styles.tableRow}>{tableHeaders1}</div>
              <div className={styles.tableRow}>{tableHeaders2}</div>
            </div>
            <div className={styles.tBody}>
              {data.length === 0 ? (
                <>
                  {SkeletonLoaderRow}
                  {SkeletonLoaderRow}
                  {SkeletonLoaderRow}
                  {SkeletonLoaderRow}
                </>
              ) : (
                renderList()
              )}
            </div>
          </>
        )
      }
    </div>
  );
};

export default Week52GainersAndLosersList;
